import { useCallback, useEffect, useRef, useState } from 'react';
import { isBridgeCropMessage } from '../bridge.js';

export interface IncomingImage {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  /** Where it came from, shown in the header. */
  label: string;
}

/**
 * Every way an image gets into the workbench: clipboard paste, drag and drop,
 * a file picker, and the extension bridge.
 *
 * Paste is the one the workflow is built around - crop in the extension, Ctrl+V
 * here - so it is bound at the window level and works no matter what has focus.
 */
export function useImageInput(onImage: (image: IncomingImage) => void): {
  dragging: boolean;
  openFilePicker: () => void;
} {
  const [dragging, setDragging] = useState(false);
  const handler = useRef(onImage);
  handler.current = onImage;

  const ingestBlob = useCallback(async (blob: Blob, label: string) => {
    // `imageOrientation: 'from-image'` matters for phone screenshots, which
    // carry EXIF rotation that would otherwise be ignored on the GPU path.
    const bitmap = await createImageBitmap(blob, {
      colorSpaceConversion: 'default',
      imageOrientation: 'from-image',
    });
    handler.current({ bitmap, width: bitmap.width, height: bitmap.height, label });
  }, []);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent): void => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (!item.type.startsWith('image/')) continue;
        const blob = item.getAsFile();
        if (!blob) continue;
        event.preventDefault();
        void ingestBlob(blob, 'pasted image');
        return;
      }
    };

    const onDragOver = (event: DragEvent): void => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      setDragging(true);
    };
    const onDragLeave = (event: DragEvent): void => {
      if (event.relatedTarget) return;
      setDragging(false);
    };
    const onDrop = (event: DragEvent): void => {
      const file = event.dataTransfer?.files?.[0];
      setDragging(false);
      if (!file || !file.type.startsWith('image/')) return;
      event.preventDefault();
      void ingestBlob(file, file.name);
    };

    const onMessage = (event: MessageEvent): void => {
      // Only same-window, same-origin messages in the documented envelope.
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (!isBridgeCropMessage(event.data)) return;
      const { dataUrl, pageUrl } = event.data.payload;
      void fetch(dataUrl)
        .then((response) => response.blob())
        .then((blob) => ingestBlob(blob, pageUrl ? `crop from ${hostOf(pageUrl)}` : 'extension crop'));
    };

    window.addEventListener('paste', onPaste);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('message', onMessage);
    };
  }, [ingestBlob]);

  const openFilePicker = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) void ingestBlob(file, file.name);
    });
    input.click();
  }, [ingestBlob]);

  return { dragging, openFilePicker };
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'page';
  }
}
